/**
 * In-mission HUD plus the pause and debrief overlays (08 §2.7-2.9), as plain DOM.
 *
 * The HUD is a pure view of the sim contract: it reads `World` every frame and `SimEvent`s as
 * they happen, and never writes to either. It is laid out on a 640x360 logical canvas and the
 * root is scaled by the game's integer pixel scale, so one HUD pixel is one game pixel.
 *
 * This file only composes the pieces; each panel and overlay lives in its own module here,
 * beside the stylesheet for the classes it owns. `setOptions` switches the display modes (the
 * 1987 box, colour-blind accents, the in-world indicators of `world/`); `setProjector` tells
 * those indicators where the ground is on screen.
 */

import './base.css';
import type { MissionDef, SimEvent, World } from '../../sim/types';
import { Banners } from './banners';
import { DebriefOverlay } from './debriefOverlay';
import { el, show, Shown } from './dom';
import { FocusKeeper } from './focusKeeper';
import { arcsOn, ringMode } from './hudAids';
import { ObjectivePanel } from './objectivePanel';
import { applyViewOptions, DEFAULT_VIEW_OPTIONS, type HudOptions, type HudViewOptions } from './options';
import { OspreyTimer } from './ospreyTimer';
import { PauseOverlay } from './pauseOverlay';
import { RetroPanel } from './retroPanel';
import { StatusPanel } from './statusPanel';
import { StripMap } from './stripMap';
import { hudTextClass, HUD_TEXT_SIZES } from './textSize';
import { WeaponPanel } from './weaponPanel';
import { mapColumn } from './windArrow';
import { KnifeMark } from './world/knifeMark';
import { WorldLayer } from './world/layer';
import type { Projector } from './world/projection';

export type { HudOptions, HudViewOptions } from './options';
export type { Projector } from './world/projection';

/**
 * The in-mission HUD, pause menu and debrief. Construct once per mission, then each rendered
 * frame call `handle(events)` and `update(world)`; call `layout()` whenever the game image moves
 * or rescales. Only the overlay buttons take pointer input, so the mouse keeps aiming the game.
 */
export class Hud {
  private readonly root: HTMLDivElement;
  private readonly objective: ObjectivePanel;
  private readonly map: StripMap;
  private readonly status = new StatusPanel();
  private readonly weapons: WeaponPanel;
  private readonly retro: RetroPanel;
  private readonly inWorld: WorldLayer;
  private readonly knife = new KnifeMark();
  private readonly osprey: OspreyTimer;
  private readonly banners: Banners;
  private readonly prompt: HTMLElement;
  private readonly pause: PauseOverlay;
  private readonly debrief: DebriefOverlay;
  private readonly shown = new Shown();
  private readonly focus: FocusKeeper;

  private disposed = false;
  private paused = false;
  private pauseShown = false;
  private resultSeen = false;
  private lastTick = -1;
  private readonly view: HudViewOptions;

  constructor(
    parent: HTMLElement,
    def: MissionDef,
    private readonly options: HudOptions,
  ) {
    this.root = el('div', 'hud-root');
    this.focus = new FocusKeeper(this.root);
    this.inWorld = new WorldLayer(def.biome);
    this.objective = new ObjectivePanel(def, options.bindings);
    this.view = { ...DEFAULT_VIEW_OPTIONS };
    this.banners = new Banners(def, options.bindings);
    this.map = new StripMap(def);
    this.weapons = new WeaponPanel(options.bindings);
    this.retro = new RetroPanel(def);
    this.osprey = new OspreyTimer(def);
    this.prompt = el('div', 'hud-prompt hud-hidden');
    this.pause = new PauseOverlay(
      () => this.resumeClicked(),
      () => this.restartClicked(),
      { leaveLabel: options.leaveLabel, bindings: options.bindings, hidePlayfield: options.hidePlayfieldOnPause },
    );
    this.debrief = new DebriefOverlay(
      def,
      () => this.restartClicked(),
      () => this.syncOverlays(),
    );
    const topCentre = el('div', 'hud-tc');
    topCentre.append(this.osprey.el, this.banners.el);

    // A right-click on an overlay button must not open the browser menu mid-game.
    this.root.addEventListener('contextmenu', (ev) => ev.preventDefault());
    // The in-world layer goes over the corner panels: a sound pip must never hide behind one.
    this.root.append(
      this.objective.el,
      this.retro.el,
      mapColumn(this.map.el, def),
      this.status.el,
      this.weapons.el,
      this.inWorld.el,
      this.knife.el,
      topCentre,
      this.prompt,
      this.pause.el,
      this.debrief.el,
    );
    parent.append(this.root);
    // Always applied, so the aids follow the difficulty even when the app passes no options at all.
    this.setOptions(options.view ?? {});
  }

  /** Change display options at any time: the 1987 box, colour-blind accents, text size, in-world indicators. */
  setOptions(options: Partial<HudViewOptions>): void {
    if (this.disposed) return;
    const view = this.view;
    applyViewOptions(view, options);
    this.root.classList.toggle('hud-1987', view.hud1987);
    this.root.classList.toggle('hud-cb', view.colourBlind);
    const wanted = hudTextClass(view.textSize);
    for (const size of HUD_TEXT_SIZES) {
      const name = hudTextClass(size);
      if (name !== '') this.root.classList.toggle(name, name === wanted);
    }
    this.objective.setTextSize(view.textSize);
    this.map.setColourBlind(view.colourBlind);
    // The aids thin out as the difficulty rises (hudAids.ts); `'on'` from Options always wins.
    this.inWorld.setOptions({
      arcs: arcsOn(view.detectionArcs, view.difficulty),
      ring: ringMode(view.noiseRing, view.difficulty),
      soundPips: view.soundPips,
    });
  }

  /** The map / tactical key is held: the standing orders come back in full for as long as it is down. */
  setOrdersHeld(held: boolean): void {
    if (this.disposed) return;
    this.objective.setOrdersHeld(held);
  }

  /**
   * How the in-world indicators find the ground on screen: a ground point (x, z) to viewport
   * CSS pixels - the space `layout`'s rect is in - or null when it is off the game image. It must
   * not round: the HUD samples it to recover the camera's scale. Null switches the indicators off.
   */
  setProjector(projector: Projector | null): void {
    if (this.disposed) return;
    this.inWorld.setProjector(projector);
    this.knife.setProjector(projector);
  }

  /**
   * ROUND7 §2: the ground point the feet of the man a stab would take are drawn at (`knifeTarget`, whatever weapon
   * is out), or null for nobody in reach. Marked with a small bracket until the next call.
   */
  setKnifeTarget(target: { x: number; z: number } | null): void {
    if (!this.disposed) this.knife.set(target);
  }

  /**
   * Call once per rendered frame with the current world. Cheap: every field is compared with
   * what is already on screen and the DOM is touched only on a change; the strip map redraws
   * at most ten times a second. Also raises the debrief once `world.result` is set.
   */
  update(world: World): void {
    if (this.disposed) return;
    if (world.tick < this.lastTick) {
      this.banners.clear();
      this.inWorld.clear();
    }
    this.lastTick = world.tick;

    this.banners.observe(world);
    this.osprey.update(world);
    // Both HUD modes are kept current (unchanged fields cost nothing), so switching is instant.
    this.objective.update(world);
    this.status.update(world);
    this.weapons.update(world);
    this.retro.update(world);
    const now = performance.now();
    this.map.draw(world, now);
    this.inWorld.update(world, now);

    if (world.result === null) {
      if (this.resultSeen) {
        this.resultSeen = false;
        this.debrief.hide();
      }
    } else if (!this.resultSeen) {
      this.resultSeen = true;
      this.debrief.schedule(world.result);
    }
  }

  /** One-shot sim events for this frame: alert, Osprey, objective, pickup and casualty banners. */
  handle(events: readonly SimEvent[]): void {
    if (this.disposed) return;
    this.banners.handle(events);
    this.inWorld.handle(events);
  }

  /**
   * Fit the HUD over the game image. `rect` is the box the image covers, in viewport CSS pixels,
   * and `scale` its pixel scale; the HUD keeps a `rect / scale` logical canvas (640x360) and is
   * scaled from the box's top-left corner so HUD pixels land on game pixels.
   */
  layout(rect: { left: number; top: number; width: number; height: number }, scale: number): void {
    if (this.disposed) return;
    const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
    // `changed` comes first in each line so every field is recorded, never short-circuited.
    let dirty = this.shown.changed('layoutLeft', rect.left);
    dirty = this.shown.changed('layoutTop', rect.top) || dirty;
    dirty = this.shown.changed('layoutWidth', rect.width) || dirty;
    dirty = this.shown.changed('layoutHeight', rect.height) || dirty;
    dirty = this.shown.changed('layoutScale', s) || dirty;
    if (!dirty) return;
    this.inWorld.setFrame({ left: rect.left, top: rect.top, scale: s }, rect.width / s, rect.height / s);
    this.knife.setFrame({ left: rect.left, top: rect.top, scale: s });
    const style = this.root.style;
    style.left = `${rect.left}px`;
    style.top = `${rect.top}px`;
    style.width = `${rect.width / s}px`;
    style.height = `${rect.height / s}px`;
    style.transform = `scale(${s})`;
  }

  /** Show or hide the pause menu. While the debrief is up it takes precedence over the menu. */
  setPaused(paused: boolean): void {
    if (this.disposed || paused === this.paused) return;
    this.paused = paused;
    // The pause menu is the one place the bindings can have changed since the hints were drawn.
    if (!paused) {
      this.weapons.refreshKeys();
      this.objective.refreshKeys();
    }
    this.root.classList.toggle('hud-paused', paused);
    this.syncOverlays();
  }

  /** Extra transient text line bottom-centre, e.g. "[E] LOOT AMMO SHACK", or null to clear. */
  setPrompt(text: string | null): void {
    if (this.disposed) return;
    const value = text ?? '';
    if (!this.shown.changed('prompt', value)) return;
    this.prompt.textContent = value;
    show(this.prompt, value !== '');
  }

  /** Remove the HUD from the page and cancel everything pending. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.debrief.dispose();
    this.focus.release();
    this.root.remove();
  }

  private resumeClicked(): void {
    this.setPaused(false);
    this.options.onResume();
  }

  private restartClicked(): void {
    // `resultSeen` stays set so the old world's result cannot raise the debrief again; it
    // clears when a world without a result arrives.
    this.debrief.hide();
    this.setPaused(false);
    this.options.onRestart();
  }

  /** The debrief outranks the pause menu; keyboard focus follows whichever is up. */
  private syncOverlays(): void {
    const pauseShown = this.paused && !this.debrief.open;
    if (pauseShown !== this.pauseShown) {
      this.pauseShown = pauseShown;
      this.pause.setVisible(pauseShown);
      if (pauseShown) {
        this.focus.remember();
        this.pause.focusResume();
      }
    }
    if (!pauseShown && !this.debrief.open) this.focus.release();
  }
}
